// frontend/src/components/pricing/planFeatures.js
//
// Renders plan feature bullets that may carry inline markup.
//
// Plan copy is served from the backend (app/core/plan_content.py -> /pricing) as
// PLAIN STRINGS, so a bullet cannot hold a React element directly. Instead a
// bullet may embed the same `[label](/path)` + `**bold**` dialect the blog uses,
// which `renderInline` parses into a RouterLink / <strong>. One parser, one
// dialect, site-wide.
//
// It is imported from components/shared/inlineMarkup — NOT from the blog's
// ArticleBody, which merely re-exports it. This module is reachable from the
// EAGER bundle (App.js -> Homepage -> PricingSection), so importing it via
// ArticleBody would pull the blog's block renderers (Para/BlogTable/ListBlock)
// into the landing-page chunk, where they can never run. Keep this import
// pointed at the shared module.
//
// SECURITY: those strings arrive over the public /pricing API, so hrefs are
// untrusted; the allow-list lives in inlineMarkup.classifyHref.
//
// <PlanCard> renders each `features[i]` bare inside a <Typography>, so it
// happily accepts nodes instead of strings — the mapping belongs in the callers
// (PricingSection, Payment), and PlanCard itself needs no knowledge of markup.
//
// This module is the PLAN-CARD-ONLY seam over the shared parser: the <strong>
// styling and the multiplier chips below apply here and nowhere else, so the
// blog and Terms keep the browser-default bold they have always had.
//
// NOTE: this dialect must never reach the email layer, which interpolates
// feature strings verbatim (see plan_content._strip_inline_markup, which is what
// makes that true, plus _PRO_EMAIL_EXCLUDED_HE for bullets emails must not show
// at all).

import React from "react";
import { Box } from "@mui/material";
import { renderInline } from "../shared/inlineMarkup";

/**
 * Link styling for an in-bullet anchor, tinted to the owning card's accent so
 * the link belongs to its card rather than to the blog's default teal.
 *
 * The underline is ALWAYS on (not hover-only like the blog default): these
 * anchors sit mid-sentence in a dense list, where colour alone would be the
 * only affordance — which fails WCAG 1.4.1 (Use of Color).
 */
const linkSxForAccent = (accent) => ({
  color: accent,
  fontWeight: 700,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": { textDecoration: "underline", opacity: 0.8 },
});

/**
 * Emphasis for a `**phrase**` inside a plan bullet — ONE notch above the
 * surrounding #374151 body copy, not the browser's full bold.
 *
 * The `a strong` reset is load-bearing: one bullet emphasises a phrase that is
 * also a link label (`[**אתר עסקי מעוצב**](/business-website)`), and a link
 * already carries its own, stronger emphasis. Without the reset the neutral grey
 * would win over the accent colour and the link would stop looking like one.
 */
const BULLET_MARKUP_SX = {
  "& strong": { fontWeight: 600, color: "#1F2937" },
  "& a strong": { fontWeight: "inherit", color: "inherit" },
};

// A NON-BREAKING space, written as an escape so it cannot be mistaken for a
// plain one in review. Chrome still breaks BEFORE the chip's inline-block even
// after an NBSP (measured at 390px), so the NBSP is only the visual gap - the
// no-orphan guarantee comes from the nowrap tail below.
const NBSP = "\u00A0";

/**
 * Longest trailing phrase we are willing to make unbreakable.
 *
 * This is a WIDTH budget, not a taste one: the tail + NBSP + chip is ONE
 * unbreakable run, so it must fit the NARROWEST line box a bullet ever gets, or
 * it hangs outside its own text box (the card's 24px padding is all that stops
 * it clipping). At the narrowest phone we support the numbers are: 320px
 * viewport - 32 container - 48 card padding - 20 check icon - 12 gap = 204px of
 * text, against a chip measured at 29-40px. 16 characters of Hebrew at 13px is
 * ~104px, so the whole run lands ~147px - roughly 30% inside the line. At the
 * previous 28 it could reach ~223px, i.e. WIDER than the line box it has to sit
 * in. A tail over the cap is not a broken bullet: the chip is appended plainly
 * instead, which is the behaviour every bullet had before the binding existed.
 */
const MAX_NOWRAP_TAIL = 16;

/** True when a fragment carries no half-open `**` / link markup. */
function isMarkupBalanced(fragment) {
  if (/[[\]()]/.test(fragment)) return false;
  return (fragment.match(/\*\*/g) || []).length % 2 === 0;
}

/**
 * Split a chipped bullet into `[head, tail]` so the tail and the chip can be
 * rendered inside one `white-space: nowrap` span.
 *
 * WHY: at 390px the long bullets wrap, and Chrome puts the chip's inline-block
 * on a line of ITS OWN - a dangling pill under a full-width sentence. Binding
 * the chip to the last word moves the pair down together instead, which is how
 * a unit label is supposed to wrap.
 *
 * The split walks LEFT from the end until the tail is markup-balanced, because
 * the boundary must not fall inside a `**...**` span (one bullet ends
 * `... \u05E2\u05D3 **5 \u05EA\u05D5\u05DE\u05DB\u05D9\u05DD**`, whose last space sits inside the emphasis). Returns
 * null when no safe, short tail exists - the caller then appends the chip
 * plainly, which is the pre-existing behaviour rather than a broken one.
 */
function splitTailForChip(text) {
  let from = text.length;
  for (let step = 0; step < 4; step += 1) {
    const i = text.lastIndexOf(" ", from - 1);
    if (i <= 0) return null;
    const tail = text.slice(i + 1);
    if (tail.length > MAX_NOWRAP_TAIL) return null;
    if (isMarkupBalanced(tail)) return [text.slice(0, i + 1), tail];
    from = i;
  }
  return null;
}

/** Multiplier-chip entrance: staggered pop-in, gated on prefers-reduced-motion. */
const CHIP_STAGGER_SECONDS = 0.15;
const CHIP_BASE_DELAY_SECONDS = 0.25;

// ---------------------------------------------------------------------------
// Chip palette — contrast is COMPUTED, not asserted in a comment.
//
// The chip is an outline pill: a 6%-accent fill on the card's white background,
// an accent border, accent text at 11px/700. Painting that text in the raw
// accent fails WCAG AA (#0D9488 over its own 6% fill measures 3.49:1, #BF5867
// measures 4.06:1) — and since the chip is `aria-hidden`, a low-vision sighted
// reader is the ONLY reader it has. So the ink is darkened, in small steps,
// until it clears 4.5:1 against the fill the chip actually composites to, and
// the border is that same darkened ink at 70% (>= 3:1 against the card, the
// WCAG 1.4.11 bar for a non-text boundary). The pill stays an OUTLINE — only
// the ink and the edge get darker.
//
// The composite assumes a WHITE card background, which holds: chips render only
// inside <PlanCard>, whose card surface is #FFFFFF on both themes. The dark
// Enterprise card is a separate hand-rolled copy and passes no chips.
// ---------------------------------------------------------------------------

const CHIP_FILL_ALPHA = 0.06;
const CHIP_BORDER_ALPHA = 0.7;
const CHIP_MIN_TEXT_CONTRAST = 4.5;
const CHIP_DARKEN_STEP = 0.04;
const CARD_SURFACE = [255, 255, 255];

/** `#RRGGBB` -> `[r, g, b]`, or null for any other colour format. */
function parseHex(hex) {
  if (typeof hex !== "string" || !/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const n = parseInt(hex.slice(1), 16);
  /* eslint-disable no-bitwise */
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  /* eslint-enable no-bitwise */
}

const rgbCss = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
const rgbaCss = ([r, g, b], a) => `rgba(${r}, ${g}, ${b}, ${a})`;

/** `fg` at `alpha` painted over the opaque `bg`. */
const over = (fg, alpha, bg) =>
  fg.map((c, i) => c * alpha + bg[i] * (1 - alpha));

/** Every channel scaled by `k` — a plain, hue-preserving darken. */
const scale = (rgb, k) => rgb.map((c) => Math.round(c * k));

/** WCAG 2.x relative luminance. */
function luminance(rgb) {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two luminances. */
function contrast(a, b) {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

const chipPaletteCache = new Map();

/**
 * Fill / border / ink for a chip on a card of accent `accent`.
 *
 * A non-hex accent degrades to the pre-existing "solid accent" shape rather
 * than breaking — the two exported card themes are hex today.
 */
function chipPalette(accent) {
  const cached = chipPaletteCache.get(accent);
  if (cached) return cached;

  const rgb = parseHex(accent);
  let palette;
  if (!rgb) {
    palette = { fill: accent, border: accent, ink: accent };
  } else {
    const fillLum = luminance(over(rgb, CHIP_FILL_ALPHA, CARD_SURFACE));
    let ink = rgb;
    let k = 1;
    while (
      k > 0.2 &&
      contrast(luminance(ink), fillLum) < CHIP_MIN_TEXT_CONTRAST
    ) {
      k -= CHIP_DARKEN_STEP;
      ink = scale(rgb, k);
    }
    palette = {
      fill: rgbaCss(rgb, CHIP_FILL_ALPHA),
      border: rgbaCss(ink, CHIP_BORDER_ALPHA),
      ink: rgbCss(ink),
    };
  }

  chipPaletteCache.set(accent, palette);
  return palette;
}

/**
 * The outline pill rendered at the end of a quantified bullet ("x4", "x6").
 *
 * `aria-hidden` is deliberate: the chip restates the ratio between this plan's
 * quota and the Solo one, and BOTH numbers are already read out in full on the
 * same page. Letting a screen reader voice "x4" mid-sentence would end the
 * bullet on a fragment that reads as part of the sentence but is not.
 */
const MultiplierChip = ({ label, accent, index, animate }) => {
  const palette = chipPalette(accent);
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        display: "inline-block",
        verticalAlign: "middle",
        // The visual gap is the NBSP the caller renders before this element.
        marginInlineStart: "1px",
        padding: "1px 7px",
        borderRadius: "999px",
        border: `1.5px solid ${palette.border}`,
        background: palette.fill,
        color: palette.ink,
        fontSize: "11px",
        fontWeight: 700,
        lineHeight: 1.5,
        letterSpacing: "0.02em",
        fontFamily: '"DM Sans", sans-serif',
        whiteSpace: "nowrap",
        // Static keyframes live in index.css (`pc` prefix) — MUI re-serializes
        // an inline @keyframes on every render and Chrome restarts the
        // animation.
        ...(animate && {
          "@media (prefers-reduced-motion: no-preference)": {
            animation: `pcChipPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${
              CHIP_BASE_DELAY_SECONDS + index * CHIP_STAGGER_SECONDS
            }s both`,
          },
        }),
      }}
    >
      {label}
    </Box>
  );
};

/**
 * Map a plan's feature strings to renderable nodes, resolving inline markup.
 *
 * Strings without markup pass through as plain text fragments, so this is safe
 * to apply to every feature list uniformly.
 *
 * `linkMode: "plain"` renders a bullet's links as their LABEL TEXT and drops the
 * anchor. It exists for the CHECKOUT (/payment) and nowhere else: the plan copy
 * is one string in `plan_content.py` serving both the marketing card and the
 * checkout card, so the copy cannot diverge between the two surfaces - the
 * AFFORDANCE is what changes. Today the checkout passes it for the Professional
 * card only, whose linked bullet is the `/business-website` marketing landing
 * page: following it mid-checkout abandons the payment for a page that reads
 * just as well afterwards. The Solo card passes nothing and keeps its link live
 * (owner, 2026-08-11) - it points at `/supporter-network`, a public feature page
 * that renders for authenticated and anonymous readers alike. The marketing
 * surfaces pass nothing at all.
 *
 * `chips` maps a BULLET STRING to a multiplier label. It is keyed by the exact
 * bullet because the backend keys it that way (plan_content.card_chips, guarded
 * against drift in tests/test_plan_card_chips.py) - a reworded bullet loses its
 * chip rather than mislabelling one. `animateChips` should follow the card's
 * in-view state so the pop-in plays when the card is actually on screen; the
 * checkout has no observer and passes the default.
 *
 * @param {string[]} features Bullet strings (possibly carrying inline markup).
 * @param {string}   accent   Card accent colour for anchors and chips.
 * @param {{linkMode?: "link"|"plain", chips?: Object, animateChips?: boolean}} [options]
 * @returns {Array<React.ReactNode>} One node per bullet, in order.
 */
export function renderPlanFeatures(features, accent, options) {
  const sx = linkSxForAccent(accent);
  const chips = options?.chips;
  const animateChips = options?.animateChips !== false;
  let chipIndex = 0;

  // OWN properties only, and only string values. Both the map and the key come
  // off the public /pricing payload, which this module treats as untrusted: a
  // bullet worded exactly `constructor` or `toString` would otherwise resolve
  // to an inherited Object.prototype member - truthy, and then handed to React
  // as a child, which throws and blanks the pricing page AND the checkout.
  const chipFor = (feature) => {
    if (!chips || typeof chips !== "object") return null;
    if (!Object.prototype.hasOwnProperty.call(chips, feature)) return null;
    const label = chips[feature];
    return typeof label === "string" && label ? label : null;
  };

  return (features || []).map((feature, i) => {
    if (typeof feature !== "string") return feature;

    const chipLabel = chipFor(feature);
    // Ordinal among the CHIPPED bullets, not among all bullets - the stagger is
    // meant to read as "1, 2, 3" regardless of which bullets carry a chip.
    const chipAt = chipLabel ? chipIndex++ : -1;

    if (!chipLabel) {
      return (
        <Box component="span" key={`f${i}`} sx={BULLET_MARKUP_SX}>
          {renderInline(feature, sx, options)}
        </Box>
      );
    }

    const chip = (
      <MultiplierChip
        label={chipLabel}
        accent={accent}
        index={chipAt}
        animate={animateChips}
      />
    );
    const split = splitTailForChip(feature);

    return (
      <Box component="span" key={`f${i}`} sx={BULLET_MARKUP_SX}>
        {renderInline(split ? split[0] : feature, sx, options)}
        {split ? (
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            {renderInline(split[1], sx, options)}
            {NBSP}
            {chip}
          </Box>
        ) : (
          <React.Fragment>
            {NBSP}
            {chip}
          </React.Fragment>
        )}
      </Box>
    );
  });
}

export default renderPlanFeatures;
